import { Alert, Box, Button, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppLang } from '../../../../app/features/lang/providers/AppLangProvider';
import { AppLogoTextWhite } from '../../../../app/features/logo/AppLogo';
import { sanitizeHtml } from '../../../../app/utils/sanitizeHtml';
import useAuthOktaUserActivationSend from './hooks/useAuthOktaUserActivationSend';
import langAuthUserActivation from './langs/langAuthUserActivation';

type FUserActivationActivateProps = {
	emailActivation: string;
};

const FUserActivationActivate = (props: FUserActivationActivateProps) => {
	const { emailActivation } = props;
	const theme = useTheme();
	const { t } = useTranslation();
	const { language } = useAppLang();

	const {
		mutate: userActivateSend,
		isSuccess: activateSuccess,
		isError: activateError,
		isLoading: activateLoading,
	} = useAuthOktaUserActivationSend();

	const handleActivate = () => {
		userActivateSend({ login: emailActivation, locale: language });
	};

	// No account status lookup: the page must look the same for any email (ITP-11640).
	return (
		<Box sx={{ padding: '10px 0px', textAlign: 'center', color: 'white' }}>
			<Box sx={{ margin: '50px 0px' }}>
				<AppLogoTextWhite style={{ height: '68px' }} />
			</Box>
			<Box sx={{ fontSize: '2.2rem', fontWeight: 600, lineHeight: '1.5' }}>
				{t(langAuthUserActivation.text_activation_title.tag)}
			</Box>
			<Box
				sx={{
					fontSize: '1.8rem',
					fontWeight: 400,
					lineHeight: '2',
					'& i': {
						color: theme.palette.secondary.main,
					},
				}}
			>
				{t(langAuthUserActivation.text_activation_subtitle.tag)} <i>{t(langAuthUserActivation.naming.tag)}</i>
			</Box>
			<Box sx={{ margin: '25px 0px 50px' }}>
				{activateError ? (
					<Alert severity="error" sx={{ marginBottom: '25px' }}>
						<div
							dangerouslySetInnerHTML={{
								__html: t(langAuthUserActivation.error_activation.tag, {
									interpolation: { escapeValue: false },
								}),
							}}
						/>
					</Alert>
				) : (
					''
				)}
				{activateSuccess ? (
					<Alert severity="success">
						<div
							dangerouslySetInnerHTML={{
								// nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- sanitized via DOMPurify; first-party i18n markup, email is self-reflected user input
								__html: sanitizeHtml(
									t(langAuthUserActivation.success_activation.tag, {
										email: emailActivation,
										interpolation: { escapeValue: false },
									}),
								),
							}}
						/>
					</Alert>
				) : (
					<Button
						color="secondary"
						variant="contained"
						onClick={() => handleActivate()}
						disabled={activateLoading}
					>
						{t(langAuthUserActivation.btn_activation.tag)}
					</Button>
				)}
			</Box>
		</Box>
	);
};

export default FUserActivationActivate;
